import { Link } from "react-router-dom";
import { ArrowRight, Check, Ruler, Layers, Sparkles } from "lucide-react";
import { MaskedLine, Reveal } from "@/components/Reveal";
import { Marquee } from "@/components/Marquee";
import { Seo } from "@/components/Seo";
import { SERVICES } from "@/data/content";

const process = [
  {
    icon: Ruler,
    title: "Survey & Measure",
    desc: "We visit, measure to the millimetre and assess your subfloor before quoting.",
  },
  {
    icon: Layers,
    title: "Subfloor Preparation",
    desc: "Levelling, moisture barriers and acoustic underlay — the invisible craft beneath.",
  },
  {
    icon: Sparkles,
    title: "Install & Finish",
    desc: "Dust-managed installation, hand-finished edges and a spotless handover.",
  },
];

export default function Services() {
  return (
    <div data-testid="services-page" className="pt-20">
      <Seo
        title="Hardwood, LVT, Tiling & Carpet Services — COC Flooring Ltd"
        description="Six flooring disciplines — hardwood, laminate, LVT, tiling, carpet and restoration — installed by time-served fitters. See services and guide prices."
      />
      <section className="py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16">
          <MaskedLine delay={0.1}>
            <p className="font-mono text-xs uppercase tracking-[0.25em] text-primary font-semibold">
              Services
            </p>
          </MaskedLine>
          <h1 className="mt-4 font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight max-w-3xl">
            <MaskedLine delay={0.2}>Every surface,</MaskedLine>
            <MaskedLine delay={0.32}>
              <em className="italic font-light text-primary">mastered.</em>
            </MaskedLine>
          </h1>
          <MaskedLine delay={0.45}>
            <p className="mt-6 max-w-2xl text-muted-foreground font-light text-base sm:text-lg leading-relaxed">
              From heritage parquet to commercial carpet tiles — six core
              disciplines delivered by time-served fitters, never subcontracted.
            </p>
          </MaskedLine>
        </div>
      </section>

      <Marquee />

      <section className="py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 grid grid-cols-1 md:grid-cols-2 gap-10">
          {SERVICES.map((s, i) => (
            <Reveal key={s.id} delay={(i % 2) * 0.1}>
              <article
                data-testid={`service-detail-${s.id}`}
                className="group bg-card border border-border rounded-2xl overflow-hidden hover:border-primary/50 transition-colors duration-500"
              >
                <div className="overflow-hidden aspect-[16/9]">
                  <img
                    src={s.img}
                    alt={s.title}
                    loading="lazy"
                    className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                  />
                </div>
                <div className="p-8">
                  <div className="flex items-center justify-between gap-4">
                    <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-primary">
                      0{i + 1}
                    </span>
                    <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                      from £{s.range[0]}/m²
                    </span>
                  </div>
                  <h2 className="mt-3 font-serif text-xl sm:text-2xl">{s.title}</h2>
                  <p className="mt-3 text-sm text-muted-foreground font-light leading-relaxed">
                    {s.desc}
                  </p>
                  <ul className="mt-5 flex flex-wrap gap-2">
                    {s.tags.map((t) => (
                      <li
                        key={t}
                        className="inline-flex items-center gap-1.5 text-xs text-muted-foreground border border-border rounded-full px-3 py-1.5"
                      >
                        <Check size={12} className="text-primary" /> {t}
                      </li>
                    ))}
                  </ul>
                  <Link
                    to="/contact"
                    data-testid={`service-quote-${s.id}`}
                    className="mt-6 inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-primary hover:gap-3 transition-all duration-300"
                  >
                    Get a quote <ArrowRight size={14} />
                  </Link>
                </div>
              </article>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="py-16 lg:py-24 bg-[#181715] text-[#FAF8F5]">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16">
          <Reveal>
            <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl tracking-tight">
              How every project runs
            </h2>
          </Reveal>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-8">
            {process.map((p, i) => (
              <Reveal key={p.title} delay={i * 0.12}>
                <div className="border border-[#2E2C28] rounded-xl p-8 h-full">
                  <p.icon size={26} className="text-[#C87D32]" />
                  <div className="mt-6 font-mono text-[10px] uppercase tracking-[0.25em] text-[#A39E93]">
                    Step 0{i + 1}
                  </div>
                  <h3 className="mt-2 font-serif text-xl">{p.title}</h3>
                  <p className="mt-3 text-sm text-[#A39E93] font-light leading-relaxed">
                    {p.desc}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
